import { Link } from "react-router-dom";
import { ArrowLeft, Home, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SpinningIco } from "@/components/brand/SpinningIco";

/**
 * 404 page - keeps tone consistent with the rest of the site (dark
 * card on volt halo, big typographic statement, two CTAs back into
 * the site). Helpful links cover the three places people are most
 * likely meant to be heading.
 */
export function NotFoundPage() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 -z-10 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25 blur-3xl"
        style={{
          backgroundImage:
            "radial-gradient(closest-side, hsl(var(--volt)), transparent 70%)",
        }}
      />

      <div className="mx-auto flex max-w-2xl flex-col items-center gap-8 px-6 py-24 text-center sm:py-32">
        {/* Spinning ico above the numbers - dead pages get a brand
            beat too. */}
        <SpinningIco size={100} />

        {/* Big volt 404 */}
        <div className="flex items-baseline gap-1 font-black leading-none">
          <span className="text-[88px] text-volt sm:text-[128px]">4</span>
          <span className="text-[88px] text-foreground sm:text-[128px]">
            0
          </span>
          <span className="text-[88px] text-volt sm:text-[128px]">4</span>
        </div>

        <div className="max-w-md space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Airball.
          </h1>
          <p className="text-muted-foreground">
            The page you're after doesn't exist, got moved, or hasn't been
            built yet. Either way, here's your rebound.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button asChild size="lg">
            <Link to="/">
              <Home className="h-4 w-4" aria-hidden />
              Back home
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/venues">
              For venues
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>

        <div className="mt-2 flex flex-col items-center gap-1 text-xs text-muted-foreground">
          <p>You could also try:</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/about" className="hover:text-foreground">
              About Whistll
            </Link>
            <span aria-hidden>·</span>
            <Link to="/privacy-policy" className="hover:text-foreground">
              Privacy
            </Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <span aria-hidden>·</span>
            <a
              href="mailto:hello@whistll.com"
              className="hover:text-foreground"
            >
              Email us
            </a>
          </div>
        </div>

        <button
          type="button"
          onClick={() => history.back()}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          Go back to the last page
        </button>
      </div>
    </section>
  );
}
